import React from 'react';
import AiActivityDots from './AiActivityDots';

function truncate(str, max = 72) {
  if (!str || str.length <= max) return str || '';
  return `${str.slice(0, max - 1)}…`;
}

export function inferClientTasks(text) {
  const raw = String(text || '').trim();
  if (!raw) return { multiTask: false, tasks: [] };

  let segments = raw.split(/\n\s*\d+[\.)]\s+/).map((s) => s.trim()).filter(Boolean);
  if (segments.length <= 1) {
    segments = raw.split(/\n\s*[-•*]\s+/).map((s) => s.trim()).filter(Boolean);
  }
  if (segments.length <= 1) {
    segments = raw
      .split(/\s+(?:and|also|then|plus|as well as)\s+|;\s*|\s*\|\s*/i)
      .map((s) => s.trim())
      .filter((s) => s.length > 8);
  }
  if (segments.length <= 1) segments = [raw];

  return {
    multiTask: segments.length > 1,
    tasks: segments.map((seg, i) => ({
      id: `intent-${i + 1}`,
      label: truncate(seg.replace(/\s+/g, ' ')),
      status: 'pending',
      kind: 'intent',
    })),
  };
}

function TaskStatus({ status }) {
  if (status === 'completed') {
    return (
      <span className="aiChatTaskStatus aiChatTaskStatus--done" aria-label="Done">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
    );
  }
  if (status === 'failed') {
    return (
      <span className="aiChatTaskStatus aiChatTaskStatus--failed" aria-label="Failed">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
        </svg>
      </span>
    );
  }
  if (status === 'cancelled') {
    return (
      <span className="aiChatTaskStatus aiChatTaskStatus--cancelled" aria-label="Stopped">
        <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <rect x="6" y="6" width="12" height="12" rx="1" />
        </svg>
      </span>
    );
  }
  if (status === 'in_progress') {
    return (
      <span className="aiChatTaskStatus aiChatTaskStatus--active" aria-label="In progress">
        <span className="aiChatTaskStatusRing" />
      </span>
    );
  }
  return <span className="aiChatTaskStatus aiChatTaskStatus--pending" aria-hidden="true" />;
}

export default function AiChatTaskList({
  taskPlan,
  active = false,
  showLeaveNotice = false,
}) {
  const tasks = taskPlan?.tasks || [];
  if (!tasks.length) return null;

  const completed = tasks.filter((t) => t.status === 'completed').length;
  const total = tasks.length;
  const inProgress = tasks.some((t) => t.status === 'in_progress');
  const activeTask = tasks.find((t) => t.status === 'in_progress');
  const wasStopped = tasks.some((t) => t.status === 'cancelled') && !inProgress;
  const allDone = completed === total && !inProgress && !wasStopped;

  return (
    <div className={`aiChatTaskPanel${active ? ' aiChatTaskPanel--active' : ''}${allDone ? ' aiChatTaskPanel--done' : ''}`}>
      {showLeaveNotice && active && !allDone && (
        <p className="aiChatTaskLeaveNotice" role="status">
          You can leave — we&apos;ll keep working in the background
        </p>
      )}

      <div className="aiChatTaskHeader">
        {active && !allDone ? (
          <span className="aiChatTaskHeaderLive">
            {activeTask ? 'Working on' : 'Planning'}
            <AiActivityDots />
          </span>
        ) : wasStopped ? (
          <span className="aiChatTaskHeaderLabel">Stopped</span>
        ) : (
          <span className="aiChatTaskHeaderLabel">
            {allDone ? 'Done' : 'Tasks'}
          </span>
        )}
        {total > 1 && (
          <span className="aiChatTaskCount">{completed}/{total}</span>
        )}
      </div>

      <ul className="aiChatTaskList">
        {tasks.map((task) => (
          <li
            key={task.id}
            className={`aiChatTaskItem aiChatTaskItem--${task.status || 'pending'}`}
          >
            <TaskStatus status={task.status} />
            <span className={`aiChatTaskLabel${task.status === 'in_progress' ? ' aiChatTaskLabel--shimmer' : ''}`}>
              {task.label}
            </span>
            {task.error && (
              <span className="aiChatTaskError">{task.error}</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
